import axios from 'axios';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { env } from '../../shared/config/env';

const API_BASE_URL = env.adminApi;

let onUnauthorized = null;

export const setUnauthorizedHandler = (handler) => {
  onUnauthorized = handler;
};

const getAuthHeaders = async () => {
  const { accessToken } = (await fetchAuthSession()).tokens ?? {};
  if (!accessToken) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${accessToken.toString()}`,
    'Content-Type': 'application/json',
  };
};

const handleUnauthorized = async () => {
  try {
    await signOut();
  } catch {
    /* ignore */
  }
  localStorage.removeItem('user_id');
  localStorage.removeItem('email');
  if (onUnauthorized) {
    onUnauthorized();
  } else if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
    window.location.assign('/login');
  }
};

const handleApiError = async (error) => {
  console.error('API Error:', error);

  if (error.response?.status === 401) {
    await handleUnauthorized();
  }

  let errorMessage = 'An error occurred. Please try again.';

  if (error.response) {
    if (error.response.data && error.response.data.message) {
      errorMessage = error.response.data.message;
    } else {
      errorMessage = error.response.statusText || errorMessage;
    }
  } else if (error.message) {
    errorMessage = error.message;
  }

  return new Error(errorMessage);
};

const compactQueryParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );

const apiClient = {
  get: async (endpoint, params = {}) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
        headers,
        params: compactQueryParams(params),
      });
      return response.data;
    } catch (error) {
      throw await handleApiError(error);
    }
  },

  post: async (endpoint, data = {}) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API_BASE_URL}${endpoint}`, data, {
        headers,
      });
      return response.data;
    } catch (error) {
      throw await handleApiError(error);
    }
  },

  patch: async (endpoint, data = {}) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.patch(`${API_BASE_URL}${endpoint}`, data, {
        headers,
      });
      return response.data;
    } catch (error) {
      throw await handleApiError(error);
    }
  },

  delete: async (endpoint) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.delete(`${API_BASE_URL}${endpoint}`, {
        headers,
      });
      return response.data;
    } catch (error) {
      throw await handleApiError(error);
    }
  },

  getBlob: async (endpoint, params = {}) => {
    try {
      const headers = await getAuthHeaders();
      delete headers['Content-Type'];
      const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
        headers,
        params: compactQueryParams(params),
        responseType: 'blob',
        validateStatus: () => true,
      });
      if (response.status === 401) {
        await handleUnauthorized();
        throw new Error('Not authenticated');
      }
      if (response.status !== 200) {
        let message = 'Request failed';
        if (response.data instanceof Blob) {
          const text = await response.data.text();
          try {
            const parsed = JSON.parse(text);
            message = parsed.message || message;
          } catch {
            message = text || message;
          }
        }
        throw new Error(message);
      }
      return response;
    } catch (error) {
      if (error.response?.status === 401) {
        await handleUnauthorized();
      }
      throw error;
    }
  },
};

export default apiClient;
